<template>
  <div class="login-container">
    <div v-if="!hasCompany && accountType !== ACCOUNT_TYPE.CANDIDATE" class="container-title">{{ $t('login.common.title.login' ) }}</div>
    <div v-if="showRegisterLink && accountType !== ACCOUNT_TYPE.CANDIDATE" class="container-sub-title">
      <span v-if="accountType === ACCOUNT_TYPE.EMPLOYEE">{{ $t('login.common.message.registerTip.noAccount') }}</span>
      <router-link class="register-new link" :to="{ path: registerLink, query: $route.query, hash: $route.hash }">{{ $t(`login.common.message.registerTip.${accountType === ACCOUNT_TYPE.CANDIDATE ? 'registerByEmail' : 'register'}`) }}</router-link>
    </div>
    <div class="content-form login-form">
      <div class="form-line" :class="getErrorClass(message.account)">
        <el-input
          v-model="account"
          name="account"
          class="line-input"
          :placeholder="accountPlaceholder"
          @change="onAccountChange"
          @focus="onAccountFocus"
          @blur="onAccountBlur"
          @keypress.enter.native="onAccountArrowClick"
        >
          <template v-if="onlyAccount" #suffix>
            <i class="arrow-icon iconfont icona-Loginarrow" :class="account && 'is-active'" @click="onAccountArrowClick"></i>
          </template>
        </el-input>
        <div v-show="message.account.show" class="line-tip">{{ selectLabelI18n(message.account, 'text') }}</div>
      </div>
      <div v-show="!onlyAccount" class="form-line" :class="getErrorClass(message.password)">
        <el-input
          v-model="password"
          ref="password"
          name="password"
          class="line-input"
          :type="viewPassword ? 'text' : 'password'"
          :placeholder="$t('login.common.placeholder.password')"
          @change="onPasswordChange"
          @focus="onPasswordFocus"
          @blur="onPasswordBlur"
          @keypress.enter.native="onLoginBtnClick"
        >
          <template #suffix>
            <i class="password-icon iconfont" :class="viewPassword ? 'iconVisible' : 'iconHide1'" @click="onViewPassword"></i>
          </template>
        </el-input>
        <div v-show="message.password.show" class="line-tip">{{ selectLabelI18n(message.password, 'text') }}</div>
      </div>
    </div>
    <div v-if="!onlyAccount" class="button-container">
      <el-button class="login-btn" type="primary" @click="onLoginBtnClick">{{ $t('login.common.btn.login') }}</el-button>
    </div>
    <div class="form-bottom">
      <div class="bottom-line">
        <div class="forget-password">
          <router-link class="forget-password-link" :to="{ path: `/passport/${accountType}/forget-password`, query: { ...$route.query, reset_by: 'email' }, hash: $route.hash }">{{ $t('login.common.btn.forgetPassword') }}</router-link>
        </div>
      </div>
      <div class="bottom-line">
        <el-checkbox v-model="agree" class="policy-checkbox">
          <span>{{ $t('login.common.message.policy.tip') }}</span>
          <a class="link" href="/tos/" target="_blank">{{ $t('login.common.message.policy.userAgreement') }}</a>
          <span> & </span>
          <a class="link" href="/policy/" target="_blank">{{ $t('login.common.message.policy.privacyPolicy') }}</a>
        </el-checkbox>
        <div class="line-tip"></div>
      </div>
    </div>
    <div v-if="company?.root_company_id !== ROOT_COMPANY_ID.MERCEDES_BENZ && showOtherAccount" class="other-account-container">
      <div class="split-line">
        <span class="line-text">{{ $t('login.common.label.loginOtherAccount') }}</span>
      </div>
      <div class="other-account-list">
        <el-tooltip v-if="!onlyShowLinkedin" effect="light" popper-class="light-tooltip-popper" :content="$t('login.common.label.otherAccount.51job')">
          <router-link class="other-account 51job" :to="{ path: `/passport/${ACCOUNT_TYPE.CANDIDATE}/${THIRD_PARTY_CHANNEL_TYPE_51}/third-party`, query: $route.query, hash: $route.hash }">
            <i class="account-icon iconfont icona-51job"></i>
          </router-link>
        </el-tooltip>
        <el-tooltip v-if="!onlyShowLinkedin" effect="light" popper-class="light-tooltip-popper" :content="$t('login.common.label.otherAccount.facebook')">
          <span class="other-account facebook" @click="onFacebookIconClick">
            <i class="account-icon iconfont iconfacebook"></i>
          </span>
        </el-tooltip>
        <el-tooltip effect="light" popper-class="light-tooltip-popper" :content="$t('login.common.label.otherAccount.linkedin')">
          <a class="other-account linkedin" target="_blank" rel="opener" :href="linkedinLoginLink">
            <i class="account-icon iconfont iconLinkedin1"></i>
          </a>
        </el-tooltip>
        <el-tooltip v-if="!onlyShowLinkedin" effect="light" popper-class="light-tooltip-popper" :content="$t('login.common.label.otherAccount.jobStreet')">
          <router-link class="other-account jobstreet" :to="{ path: `/passport/${ACCOUNT_TYPE.CANDIDATE}/${THIRD_PARTY_CHANNEL_TYPE_JOBSTREET}/third-party`, query: $route.query, hash: $route.hash }">
            <i class="account-icon iconfont iconGongzuojie1"></i>
          </router-link>
        </el-tooltip>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import { ACCOUNT_TYPE, HM_MOBILE_DASHBOARD_PATH, HM_PC_DASHBOARD_PATH } from '@/constant/common'
import { mixedRouterPush } from '@/router/routes'
import { THIRD_PARTY_CHANNEL_TYPE_51, THIRD_PARTY_CHANNEL_TYPE_JOBSTREET } from '@/constant/thirdParty'
import { passwordEncrypt } from '@/utils/rsa'
import { deviceType } from '@/utils/common'
import { selectLabelI18n } from '@/i18n'
import { ROOT_COMPANY_ID } from '@/constant/company'

export default {
  props: {
    company: {
      type: Object,
      default: null
    },
    linkedinLoginLink: {
      type: String,
      default: null
    },
    onlyShowLinkedin: {
      type: Boolean,
      default: true
    },
    showOtherAccount: {
      type: Boolean,
      default: false
    },
    facebookSDKLoader: {
      type: Promise,
      default: null
    }
  },
  data () {
    return {
      isMobile: deviceType.isMobile,
      selectLabelI18n,
      ROOT_COMPANY_ID,
      THIRD_PARTY_CHANNEL_TYPE_51,
      THIRD_PARTY_CHANNEL_TYPE_JOBSTREET,
      ACCOUNT_TYPE,
      account: null,
      password: null,
      viewPassword: false,
      agree: true,
      message: {
        account: {
          show: false,
          isError: false,
          text: null,
          textI18nKey: null
        },
        password: {
          show: false,
          isError: false,
          text: null,
          textI18nKey: null
        }
      },
      onlyAccount: true,
      facebookLoginLink: null
    }
  },
  computed: {
    accountType () {
      return this.$route.params.accountType
    },
    companyId () {
      return this.company?.id
    },
    hasCompany () {
      return Boolean(this.companyId)
    },
    showRegisterLink () {
      if ([ACCOUNT_TYPE.EMPLOYEE].includes(this.accountType)) {
        return this.hasCompany
      } else if ([ACCOUNT_TYPE.CANDIDATE].includes(this.accountType)) {
        return !this.hasCompany
      }
      return false
    },
    registerLink () {
      if ([ACCOUNT_TYPE.CANDIDATE, ACCOUNT_TYPE.EMPLOYEE].includes(this.accountType)) {
        return `/passport/${this.accountType}/register`
      }
      return '/'
    },
    employeeForm () {
      return this.company?.employee_form
    },
    accountPlaceholder () {
      if ([ACCOUNT_TYPE.CANDIDATE, ACCOUNT_TYPE.ALUMNI].includes(this.accountType)) {
        return this.$t('login.common.placeholder.emailOrPhone')
      } else if (this.accountType === ACCOUNT_TYPE.EMPLOYEE) {
        if (this.employeeForm?.is_customization_form) {
          if (this.employeeForm.i18nEmployeeIdNumTip) {
            return this.$t('login.common.placeholder.customEmployeeNumber', { customTip: this.employeeForm.i18nEmployeeIdNumTip })
          } else {
            return this.$t('login.common.placeholder.employeeNumber')
          }
        }
        return this.$t('login.common.placeholder.emailOrPhone')
      }
      return this.$t('login.common.placeholder.email')
    }
  },
  created () {
    this.agree = true
    this.refreshPage(true)
  },
  methods: {
    ...mapActions('account', ['login', 'checkSSO', 'getLogonAccountNextPage', 'getThirdPartyLoginInfo']),
    ...mapActions('hm', ['hmLogin', 'setToken']),
    async refreshPage (isInit) {
      _.forEach(this.message, message => message.isError && this.clearErrorMessage(message))
      if (this.$route.params.autofill_email) {
        this.account = this.$route.params.autofill_email
      }
      if (this.accountType === ACCOUNT_TYPE.EMPLOYEE || this.accountType === ACCOUNT_TYPE.HR || this.accountType === ACCOUNT_TYPE.HIRING_MANAGER) {
        this.$eventBus.$emit('showLoading', true)
        try {
          let companyIdType = 'hr_company_id'
          if (this.accountType === ACCOUNT_TYPE.EMPLOYEE) {
            companyIdType = 'employee_company_id'
          } else if (this.accountType === ACCOUNT_TYPE.HIRING_MANAGER) {
            companyIdType = 'hiring_manager_company_id'
          }
          const resp = await this.checkSSO({
            account_type: this.accountType,
            name: this.account,
            [companyIdType]: this.companyId,
            back: this.getBackUrl()
          })
          if (resp && resp.sso_path) {
            window.location.href = resp.sso_path
            return
          }
        } finally {
          this.$eventBus.$emit('showLoading', false)
        }
      }
      this.onlyAccount = ![ACCOUNT_TYPE.CANDIDATE, ACCOUNT_TYPE.ALUMNI].includes(this.accountType)
      if (this.accountType === ACCOUNT_TYPE.HIRING_MANAGER) {
        if (this.$route.query.email) {
          this.account = this.$route.query.email
        }
      }
      if (!isInit) {
        this.$eventBus.$emit('showLoading', true)
        try {
          const resp = await this.getLogonAccountNextPage({
            back: this.getBackUrl()
          })
          if (resp) {
            mixedRouterPush(resp.next_page)
            return
          }
        } finally {
          this.$eventBus.$emit('showLoading', false)
        }
      }
    },
    onAccountChange () {
      this.validate(this.message.account)
    },
    onAccountFocus () {
      this.clearErrorMessage(this.message.account)
    },
    onAccountBlur () {
      this.validate(this.message.account)
    },
    onPasswordChange () {
      this.validate(this.message.password)
    },
    onPasswordFocus () {
      this.clearErrorMessage(this.message.password)
    },
    onPasswordBlur () {
      this.validate(this.message.password)
    },
    setErrorMessage (message, textI18nKey, getter) {
      if (!message) {
        delete this.message.account.text
        _.forEach(this.message, msg => this.setErrorMessage(msg, textI18nKey, getter))
        return
      }
      if (getter) {
        Object.defineProperty(message, 'text', {
          configurable: true,
          enumerable: true,
          get: getter
        })
      } else {
        message.text = null
      }
      message.textI18nKey = textI18nKey
      message.isError = true
      message.show = true
    },
    clearErrorMessage (message) {
      message.show = false
      message.isError = false
      message.textI18nKey = null
    },
    validate (message) {
      if (!message) {
        return _.chain(this.message).map(this.validate).includes(false).value()
      }
      let hasError = false
      const textI18nKey = null
      let getter = null
      if (message === this.message.account) {
        if (!this.account) {
          hasError = true
          getter = () => this.accountPlaceholder
        }
      } else if (message === this.message.password) {
        // if (!this.password) {
        //   hasError = true
        //   textI18nKey = 'login.common.placeholder.password'
        // }
      }
      if (hasError) {
        this.setErrorMessage(message, textI18nKey, getter)
        return false
      } else {
        this.clearErrorMessage(message)
        return true
      }
    },
    async onAccountArrowClick () {
      if (!this.agree) {
        this.$message.warning(this.$t('login.common.message.agreePolicyFirst'))
        return
      }
      if (!this.validate(this.message.account)) {
        return
      }
      if ([ACCOUNT_TYPE.EMPLOYEE, ACCOUNT_TYPE.CANDIDATE, ACCOUNT_TYPE.ALUMNI].includes(this.accountType)) {
        this.onlyAccount = false
        return
      }
      this.$eventBus.$emit('showLoading', true)
      try {
        const resp = await this.checkSSO({
          account_type: this.accountType,
          name: this.account,
          employee_company_id: this.companyId,
          back: this.getBackUrl()
        })
        if (resp && resp.sso_path) {
          window.location.href = resp.sso_path
        } else {
          this.onlyAccount = false
          // this.message.account.show = false
          // const pwdMsg = this.message.password
          // pwdMsg.isError = false
          // pwdMsg.textI18nKey = this.$t('login.common.message.hrLoginTip')
          // pwdMsg.show = true
          this.$nextTick(() => {
            this.$refs.password.focus()
          })
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    onViewPassword () {
      this.viewPassword = !this.viewPassword
    },
    async onLoginBtnClick () {
      if (!this.agree) {
        this.$message.warning(this.$t('login.common.message.agreePolicyFirst'))
        return
      }
      if (this.accountType === ACCOUNT_TYPE.HIRING_MANAGER) {
        this.doHMLogin()
        return
      }
      this.$eventBus.$emit('showLoading', true)
      try {
        const hasError = this.validate()
        if (hasError) {
          return
        }
        const data = {
          name: this.account.trim(),
          password: passwordEncrypt(this.password),
          account_type: this.accountType,
          agree: this.agree,
          company_id: this.companyId,
          back: this.getBackUrl(),
          submit: this.$route.query.submit,
          apply_job_id: this.$route.query.apply_job_id,
          agreed_policy_items: this.$route.query.agreed_policy_items,
          redirect_job_id: this.$route.query.redirect_job_id,
          redirect_company_id: this.$route.query.redirect_company_id
        }
        const resp = await this.login(data)
        if (resp) {
          if (resp.sso_path) {
            window.location.href = resp.sso_path
          } else if (resp.success) {
            mixedRouterPush(resp.next_page || '/')
          } else {
            // let getter
            if (resp.lock_countdown_time) {
              // getter = () => this.$t('login.common.message.lockAccount.countdownTime', { countdownTime: parseInt(resp.lock_countdown_time) })
              this.$message.error(this.$t('login.common.message.lockAccount.countdownTime', { countdownTime: parseInt(resp.lock_countdown_time) }))
            } else if (resp.lock_after) {
              // getter = () => this.$t('login.common.message.lockAccount.lockAfter', { times: parseInt(resp.lock_after) })
              this.$message.error(this.$t('login.common.message.lockAccount.lockAfter', { times: parseInt(resp.lock_after) }))
            } else {
              // getter = () => this.$t('login.common.message.accountOrPasswordIsWrong')
              this.$message.error(this.$t('login.common.message.accountOrPasswordIsWrong'))
            }
            // this.setErrorMessage(this.message.password, null, getter)
          }
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    async doHMLogin () {
      this.$eventBus.$emit('showLoading', true)
      try {
        const hasError = this.validate()
        if (hasError) {
          return
        }
        const params = {
          email_address: this.account.trim(),
          password: passwordEncrypt(this.password),
          company_id: this.companyId
        }
        const resp = await this.hmLogin(params)
        if (resp) {
          if (resp.success) {
            mixedRouterPush(this.getBackUrl() || (deviceType.isPc ? HM_PC_DASHBOARD_PATH : HM_MOBILE_DASHBOARD_PATH))
          } else {
            // let getter
            if (resp.lock_countdown_time) {
              // getter = () => this.$t('login.common.message.lockAccount.countdownTime', { countdownTime: parseInt(resp.lock_countdown_time) })
              this.$message.error(this.$t('login.common.message.lockAccount.countdownTime', { countdownTime: parseInt(resp.lock_countdown_time) }))
            } else if (resp.lock_after) {
              // getter = () => this.$t('login.common.message.lockAccount.lockAfter', { times: parseInt(resp.lock_after) })
              this.$message.error(this.$t('login.common.message.lockAccount.lockAfter', { times: parseInt(resp.lock_after) }))
            } else {
              // getter = () => this.$t('login.common.message.accountOrPasswordIsWrong')
              this.$message.error(this.$t('login.common.message.accountOrPasswordIsWrong'))
            }
          }
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    getFacebookUserInfoAndLogin (signedRequest) {
      const apiURL = 'https://graph.facebook.com/me?fields=email,id,first_name,last_name,middle_name,name,name_format,picture,short_name,birthday'
      window.FB.api(apiURL, (userInfo) => {
        userInfo = encodeURIComponent(JSON.stringify(userInfo))
        const originUrl = encodeURIComponent(window.location.href)
        window.location.href = `/facebook-login/?state=1&profile_info=${userInfo}&signed_request=${signedRequest}&origin_url=${originUrl}`
      }, { scope: 'email' })
    },
    async onFacebookIconClick () {
      this.$eventBus.$emit('showLoading', true)
      try {
        await this.facebookSDKLoader
        const FB = window.FB
        FB.getLoginStatus((resp) => {
          if (resp.status === 'connected') {
            this.getFacebookUserInfoAndLogin(resp.authResponse.signedRequest)
          } else {
            FB.login((resp) => {
              if (resp.status === 'connected') {
                this.getFacebookUserInfoAndLogin(resp.authResponse.signedRequest)
              } else if (resp.status === 'authorization_expired') {
                // The user has signed into your application with
                // Facebook Login but must go through the login flow
                // again to renew data authorization. You might remind
                // the user they've used Facebook, or hide other options
                // to avoid duplicate account creation, but you should
                // collect a user gesture (e.g. click/touch) to launch the
                // login dialog so popup blocking is not triggered.
              } else if (resp.status === 'not_authorized') {
                // The user hasn't authorized your application.  They
                // must click the Login button, or you must call FB.login
                // in response to a user gesture, to launch a login dialog.
              } else {
                // The user isn't logged in to Facebook. You can launch a
                // login dialog with a user gesture, but the user may have
                // to log in to Facebook before authorizing your application.
              }
            })
          }
        })
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    getErrorClass (message) {
      return message && message.show && message.isError && 'has-error'
    },
    getBackUrl () {
      const query = this.$route.query
      return query.back ? `${query.back}${this.$route.hash}` : null
    }
  },
  watch: {
    accountType () {
      this.refreshPage()
    }
  }
}
</script>

<style lang="scss" scoped>
.login-container {
  position: relative;

  .container-sub-title {
    margin-top: 14px;
    color: #909c99;

    .register-new {
      &.link {
        color: var(--custom-color, #0096cc);
      }
    }
  }

  .login-form {
    margin-top: 24px;

    .form-line {
      min-height: 40px;
      margin-bottom: 24px;

      &.has-error {
        margin-bottom: 6px;

        .line-tip {
          padding-left: 0;
          color: #f56c6c;
        }

        .line-input {
          ::v-deep {
            > .el-input__inner {
              border-color: #f56c6c;
            }

            .el-input__inner:hover + .el-input-group__prepend,
            .el-input__inner:hover + .el-input-group__append,
            .el-input-group__prepend,
            .el-input-group__append {
              border-color: #f56c6c;
            }
          }
        }
      }

      .line-input {
        ::v-deep {
          &.el-input-group--prepend {
            .el-input__inner {
              border-top-left-radius: 0;
              border-bottom-left-radius: 0;
            }
          }

          &.el-input-group--append {
            .el-input__inner {
              border-top-right-radius: 0;
              border-bottom-right-radius: 0;
            }
          }

          .el-input__inner {
            border-radius: 2px;
            padding-left: 14px;
            padding-right: 35px;

            &:focus {
              border-color: var(--custom-color, #0096cc);
            }
          }

          .el-input__suffix {
            right: 14px;
          }

          .el-input-group__prepend {
            border-top-left-radius: 2px;
            border-bottom-left-radius: 2px;
            background: initial;
            transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1)
          }

          .el-input-group__append {
            border-top-right-radius: 2px;
            border-bottom-right-radius: 2px;
            background: initial;
            transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1)
          }
        }

        .arrow-icon {
          height: 40px;
          line-height: 40px;
          cursor: pointer;

          &.is-active {
            color: var(--custom-color, #0096cc);
          }
        }

        .password-icon {
          height: 40px;
          line-height: 40px;
          cursor: pointer;
          font-size: 14px;
          color: #909399;
        }
      }

      .line-tip {
        padding-left: 15px;
        font-size: 12px;
        line-height: 18px;
      }
    }
  }

  .button-container {

    .el-button {
      width: 100%;
      padding: 9px 0;
      border-radius: 2px;
      line-height: 22px;
      font-size: 16px;
      text-align: center;
      border: 0;
      background-color: var(--custom-color, #0096cc);
    }
  }

  .form-bottom {
    margin-top: 26px;

    .bottom-line {
      display: flex;
      flex-direction: column;
      margin-bottom: 10px;

      .forget-password {
        text-align: right;

        .forget-password-link {
          color: #606266;
          font-size: 12px;
          line-height: 18px;
          text-decoration: underline;

          &:hover {
            color: #717378;
            text-decoration: underline;
          }

          &:visited {
            color: #606266;
            text-decoration: underline;
          }
        }
      }

      .policy-checkbox {
        white-space: nowrap;

        .link {
          color: var(--custom-color, #0096cc);
        }

        ::v-deep {
          .el-checkbox__input {
            .el-checkbox__inner {
              width: 12px;
              height: 12px;

              &::after {
                top: 0;
                left: 3px;
              }
            }

            &.is-focus .el-checkbox__inner {
              border-color: var(--custom-color, #0096cc);
            }

            &.is-checked .el-checkbox__inner {
              background-color: var(--custom-color, #0096cc);
              border-color: var(--custom-color, #0096cc);
            }
          }

          .el-checkbox__label {
            white-space: initial;
            word-break: break-word;
            vertical-align: text-top;
            color: #606266;
            line-height: 19px;
            font-size: 12px;
            font-weight: 400;
          }
        }
      }

      .tip {
        margin-left: 22px;
        font-size: 12px;
        font-weight: 400;
        color: #606266;
      }
    }
  }

  .other-account-container {
    margin-top: 30px;
    margin-bottom: 10px;

    .split-line {
      height: 17px;
      line-height: 17px;
      text-align: center;
      position: relative;

      &::before {
        content: '';
        display: block;
        width: 100%;
        height: 1px;
        background: #dcdfe6;
        position: absolute;
        top: 9px;
        left: 0;
      }

      .line-text {
        padding: 0 6px;
        background: #ffffff;
        color: #909399;
        font-size: 12px;
        position: relative;
      }
    }

    .other-account-list {
      margin-top: 24px;
      text-align: center;

      .other-account {
        display: inline-block;
        color: #303133;
        cursor: pointer;

        .account-icon {
          font-size: 32px;
          line-height: 1;
        }

        & + .other-account {
          margin-left: 30px;
        }
      }
    }
  }
}

.is-mobile {
  .content-container-wrapper {
    .content-container {
      &.login-container {
        padding: 30px 25px;

        .container-title {
          font-size: 30px;
          line-height: 44px;
        }

        .other-account-container {
          margin: 20px 0;

          .other-account-list {
            .other-account {
              .account-icon {
                font-size: 36px;
              }
            }
          }
        }
      }
    }
  }
}
</style>
